/**
 * "Similar ETFs" strip — shown below ETF cards when current symbol is an ETF.
 * Mini-card per peer + a one-click "Pin all to Compare" CTA that uses the
 * existing comparison flow.
 */
import { useEffect, useState } from "react";
import { ArrowUpRight, Layers, Loader2, Pin } from "lucide-react";
import { getPeerEtfs } from "@/lib/api";
import EtfTypeChip from "./EtfTypeChip";

const fmtAum = (n) => {
  if (!n || !Number.isFinite(n)) return "—";
  const abs = Math.abs(n);
  if (abs >= 1e12) return `$${(n / 1e12).toFixed(2)}T`;
  if (abs >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
  if (abs >= 1e6) return `$${(n / 1e6).toFixed(0)}M`;
  return `$${n.toFixed(0)}`;
};

export default function PeerEtfsStrip({ symbol, isEtf, onPickTicker, onPinAll }) {
  const skip = !symbol || !isEtf;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (skip) {
      setData(null);
      return;
    }
    let alive = true;
    setLoading(true);
    getPeerEtfs(symbol)
      .then((d) => {
        if (alive) setData(d);
      })
      .catch(() => {
        if (alive) setData(null);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [symbol, skip]);

  if (skip) return null;
  if (loading) {
    return (
      <div
        data-testid="peer-etfs-strip"
        className="border border-zinc-800 bg-[#121214] p-4 flex items-center gap-2 text-xs font-mono text-zinc-500"
      >
        <Loader2 size={12} className="animate-spin" /> Finding similar ETFs…
      </div>
    );
  }
  const peers = data?.peers || [];
  if (peers.length === 0) {
    return (
      <div
        data-testid="peer-etfs-strip"
        className="border border-zinc-800 bg-[#121214] p-4"
      >
        <div className="text-label mb-1">Similar ETFs</div>
        <div className="text-xs text-zinc-500 font-mono">
          Not enough comparable ETFs in our DB yet. We grow the corpus daily.
        </div>
      </div>
    );
  }

  return (
    <section
      data-testid="peer-etfs-strip"
      className="border border-zinc-800 bg-[#121214] p-5"
    >
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Layers size={13} className="text-[#d7b5c9]" />
          <div className="text-label">Similar ETFs · ranked by sector overlap</div>
        </div>
        {onPinAll && (
          <button
            data-testid="pin-all-peers-btn"
            onClick={() => onPinAll(peers.map((p) => p.symbol))}
            className="inline-flex items-center gap-1.5 border border-zinc-800 hover:border-zinc-500 text-zinc-300 hover:text-white px-3 h-8 text-[10px] font-mono uppercase tracking-widest transition-colors"
          >
            <Pin size={11} /> Pin all to Compare
          </button>
        )}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
        {peers.map((p) => (
          <button
            key={p.symbol}
            data-testid={`peer-etf-${p.symbol}`}
            onClick={() => onPickTicker?.(p.symbol)}
            className="text-left border border-zinc-900 hover:border-zinc-600 bg-[#0d0d0f] p-3 transition-colors group"
          >
            <div className="flex items-start justify-between mb-1.5">
              <span className="font-mono font-bold text-zinc-100">{p.symbol}</span>
              <ArrowUpRight
                size={13}
                className="text-zinc-600 group-hover:text-zinc-200 transition-colors mt-0.5"
              />
            </div>
            <div className="text-[11px] text-zinc-300 normal-case tracking-normal leading-snug mb-2 line-clamp-2">
              {p.name}
            </div>
            {p.type && (
              <div className="mb-2">
                <EtfTypeChip type={p.type} size="sm" />
              </div>
            )}
            <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-wider text-zinc-500">
              <span>{fmtAum(p.aum)}</span>
              {p.expense_ratio != null && (
                <span>{Number(p.expense_ratio).toFixed(2)}%</span>
              )}
            </div>
            <div className="mt-2 pt-2 border-t border-zinc-900 flex items-center gap-1">
              <div className="flex-1 h-1 bg-zinc-900 overflow-hidden">
                <div
                  className="h-full bg-[#d7b5c9]"
                  style={{ width: `${Math.round((p.similarity || 0) * 100)}%` }}
                />
              </div>
              <span className="text-[9px] font-mono text-zinc-500 tabular-nums w-9 text-right">
                {Math.round((p.similarity || 0) * 100)}%
              </span>
            </div>
          </button>
        ))}
      </div>
    </section>
  );
}
